import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, PartyPopper } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ModuleCompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleName: string;
  hasNextModule: boolean;
  isLastModule: boolean;
  onContinue: () => void;
}

export function ModuleCompleteDialog({
  open,
  onOpenChange,
  moduleName,
  hasNextModule,
  isLastModule,
  onContinue,
}: ModuleCompleteDialogProps) {
  const [countdown, setCountdown] = useState(7);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!open) {
      setCountdown(7);
      setProgress(100);
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onContinue();
          return 0;
        }
        return prev - 1;
      });
      setProgress((prev) => Math.max(0, prev - (100 / 7)));
    }, 1000);

    return () => clearInterval(interval);
  }, [open, onContinue]);

  const handleContinue = () => {
    onOpenChange(false);
    onContinue();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md text-center">
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/20 rounded-full animate-pulse" />
            <div className="relative bg-primary rounded-full p-4">
              {isLastModule ? (
                <PartyPopper className="h-12 w-12 text-primary-foreground" />
              ) : (
                <CheckCircle2 className="h-12 w-12 text-primary-foreground" />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold">
              {isLastModule ? "Course Complete!" : "Congratulations!"}
            </h2>
            <p className="text-muted-foreground">
              You've completed <span className="font-semibold text-foreground">{moduleName}</span>
            </p>
            {isLastModule && (
              <p className="text-sm text-primary font-medium">
                You've finished all modules in this course!
              </p>
            )}
          </div>

          <div className="w-full space-y-2 mt-4">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {hasNextModule
                ? `Continuing to next module in ${countdown}s...`
                : `Returning in ${countdown}s...`}
            </p>
          </div>

          <Button onClick={handleContinue} className="w-full mt-2" size="lg">
            {hasNextModule ? (
              <>
                Continue to Next Module
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            ) : (
              "Done"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
